import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Form, Input, InputNumber, Select, message, Button, Table, DatePicker, Tooltip } from 'antd';
import type { InputRef } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { Consignment } from '../../model/consignment';
import { api_vendor } from '../../api/vendor';
import { api_product, api_product_lookup_code } from '../../api/product';

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: (values: any) => Promise<void>;
    editItem?: Consignment | null;
}

// The edit form is the bill as it was opened: its INITIAL deposit rows. GET
// returns every Batch of every Period, so a bill that was topped up or carried
// forward would otherwise list the same product several times -- the carried
// rows as if they were new deposits -- and trip the duplicate-product check.
const isOriginalDeposit = (it: { source_type?: string | null }) =>
    !it.source_type || String(it.source_type).toUpperCase() === 'INITIAL';

const productLabel = (p: { product_code?: string; product_name?: string; price_retail?: number | string | null }) =>
    `[${p.product_code || 'SKU'}] ${p.product_name || ''}` +
    (p.price_retail !== undefined && p.price_retail !== null ? ` (฿${Number(p.price_retail || 0).toLocaleString()})` : '');

// antd filters on `label` only when told to; with the old "children" it
// compared every typed word against undefined and never found anything.
const labelFilter = (input: string, option?: { label?: unknown }) =>
    String(option?.label ?? '').toLowerCase().includes(input.trim().toLowerCase());

interface FormProductItem {
    id?: number;
    product_id: number;
    product_code?: string;
    product_name?: string;
    product_unit_name?: string;
    // Consignment quantities are whole pieces only, and the input starts
    // EMPTY (undefined) rather than a real 0/1 default -- "0" is a visual
    // placeholder only, never a submitted value.
    quantity_received: number | undefined;
    latest_actual_remaining?: number;
    // Prices start EMPTY too and are filled from the product that gets
    // picked; a leftover 15/10 would be someone else's price posing as this
    // product's, and the shop prices consignment goods off the product card.
    selling_price_per_unit: number | undefined;
    owner_cost_per_unit: number | undefined;
}

export const ConsignmentModal: React.FC<Props> = ({
    open,
    onClose,
    onSave,
    editItem
}) => {
    const [form] = Form.useForm();
    const [vendors, setVendors] = useState<any[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<FormProductItem[]>([]);
    const [barcodeInput, setBarcodeInput] = useState('');
    const [barcodeSearching, setBarcodeSearching] = useState(false);
    const barcodeInputRef = useRef<InputRef>(null);
    const productSearchTimer = useRef<number | undefined>(undefined);

    useEffect(() => {
        if (open) {
            setBarcodeInput('');
            loadMasterData();
            if (editItem) {
                form.setFieldsValue({
                    vendor_id: editItem.vendor_id,
                    consignment_date: editItem.consignment_date ? dayjs(editItem.consignment_date) : dayjs(),
                    note: editItem.note
                });
                const mappedItems: FormProductItem[] = (editItem.items || []).filter(isOriginalDeposit).map(it => ({
                    id: it.id,
                    product_id: it.product_id,
                    product_code: it.product_code,
                    product_name: it.product_name,
                    product_unit_name: it.product_unit_name,
                    quantity_received: Number(it.quantity_received || 0),
                    latest_actual_remaining: Number(it.latest_actual_remaining || 0),
                    selling_price_per_unit: Number(it.selling_price_per_unit || 0),
                    owner_cost_per_unit: Number(it.owner_cost_per_unit || 0)
                }));
                setItems(mappedItems.length > 0 ? mappedItems : [createEmptyItem()]);
            } else {
                form.resetFields();
                form.setFieldsValue({
                    consignment_date: dayjs()
                });
                setItems([createEmptyItem()]);
            }
        }
    }, [open, editItem]);

    const createEmptyItem = (): FormProductItem => ({
        product_id: 0,
        quantity_received: undefined,
        selling_price_per_unit: undefined,
        owner_cost_per_unit: undefined
    });

    const loadMasterData = async () => {
        try {
            const [vendorRes, prodRes] = await Promise.all([
                api_vendor('LIST', {}),
                api_product('LIST', { page: 1, page_size: 200 })
            ]);
            setVendors(Array.isArray(vendorRes) ? vendorRes : (vendorRes?.items || []));
            setProducts(Array.isArray(prodRes) ? prodRes : (prodRes?.items || []));
        } catch (e) {
            console.error('Failed to load master data for consignment', e);
        }
    };

    const isLocked = useMemo(() => {
        if (!editItem) return false;
        return Boolean(
            editItem.has_operational_movements ||
            editItem.status === 'SETTLED' ||
            (editItem.items || []).some(it => Number(it.batch_no || 1) > 1 || Number(it.quantity_sold || 0) > 0 || Boolean(it.last_checked_at))
        );
    }, [editItem]);

    // Only the first page of products is loaded up front; a product the bill
    // already holds may sit beyond it, so the bill's own rows supply their
    // names, and typing searches the whole catalogue on the Backend.
    const productOptions = useMemo(() => {
        const byId = new Map<number, { value: number; label: string }>();
        products.forEach(p => byId.set(Number(p.id), { value: Number(p.id), label: productLabel(p) }));
        items.forEach(it => {
            if (it.product_id && !byId.has(it.product_id) && it.product_name) {
                byId.set(it.product_id, { value: it.product_id, label: productLabel(it) });
            }
        });
        return Array.from(byId.values());
    }, [products, items]);

    const handleProductSearch = (text: string) => {
        window.clearTimeout(productSearchTimer.current);
        const keyword = text.trim();
        if (!keyword) return;
        productSearchTimer.current = window.setTimeout(async () => {
            try {
                const res = await api_product('LIST', { page: 1, page_size: 50, search: keyword });
                const found: any[] = Array.isArray(res) ? res : (res?.items || []);
                if (found.length === 0) return;
                setProducts(prev => {
                    const known = new Set(prev.map(p => Number(p.id)));
                    return [...prev, ...found.filter(p => !known.has(Number(p.id)))];
                });
            } catch (e) {
                console.error('Product search for consignment failed', e);
            }
        }, 300);
    };

    useEffect(() => () => window.clearTimeout(productSearchTimer.current), []);

    const handleAddItem = () => {
        if (isLocked) return;
        setItems(prev => [...prev, createEmptyItem()]);
    };

    const handleRemoveItem = (index: number) => {
        if (isLocked) return;
        if (items.length <= 1) {
            message.warning('ต้องมีรายการสินค้าอย่างน้อย 1 รายการในรอบการฝากขายนี้');
            return;
        }
        setItems(prev => prev.filter((_, i) => i !== index));
    };

    // Both prices follow the product that was picked: its shop price and its
    // cost, exactly what the shop fills in by hand today. A product without
    // one leaves that box empty rather than keeping the previous product's
    // number -- 1,368 of 2,607 products carry no cost.
    const applyProductToItem = (current: FormProductItem, prod: any): FormProductItem => {
        const retailPrice = Number(prod?.price_retail || 0);
        const productCost = Number(prod?.product_cost || 0);
        return {
            ...current,
            product_id: Number(prod?.id || 0),
            product_code: prod?.product_code,
            product_name: prod?.product_name,
            product_unit_name: prod?.unit_name || prod?.product_unit_name || 'ชิ้น',
            selling_price_per_unit: retailPrice > 0 ? retailPrice : undefined,
            owner_cost_per_unit: productCost > 0 ? productCost : undefined
        };
    };

    const handleItemProductChange = (index: number, productId: number) => {
        if (isLocked) return;
        const prod = products.find(p => p.id === productId);
        setItems(prev => {
            const copy = [...prev];
            copy[index] = prod
                ? applyProductToItem(copy[index], prod)
                : { ...copy[index], product_id: productId };
            return copy;
        });
    };

    const handleBarcodeLookup = async (explicitCode?: string) => {
        if (isLocked) return;
        const code = String(explicitCode ?? barcodeInput).trim();
        if (!code) {
            message.warning('กรุณาสแกนหรือกรอกบาร์โค้ดสินค้า');
            return;
        }

        setBarcodeSearching(true);
        try {
            const normalizedCode = code.toLowerCase();
            let product = products.find(prod =>
                [prod.product_code, prod.barcode, prod.sku]
                    .some(value => String(value || '').trim().toLowerCase() === normalizedCode)
            );

            if (!product) {
                product = await api_product_lookup_code(code);
            }

            if (!product?.id) {
                message.warning(`ไม่พบสินค้าสำหรับบาร์โค้ด “${code}”`);
                return;
            }

            setProducts(prev => prev.some(prod => Number(prod.id) === Number(product.id))
                ? prev
                : [...prev, product]);
            setItems(prev => {
                const existingIndex = prev.findIndex(item => Number(item.product_id) === Number(product.id));
                if (existingIndex >= 0) {
                    return prev.map((item, index) => index === existingIndex
                        ? { ...item, quantity_received: Number(item.quantity_received || 0) + 1 }
                        : item);
                }

                // A barcode scan is itself a real, user-driven count of 1 --
                // this is not the "empty placeholder 0" default case (that
                // applies only to a fresh row nobody has scanned/typed into
                // yet), so the first scan of a new product sets quantity to
                // a real 1, not the empty/undefined placeholder state.
                const emptyIndex = prev.findIndex(item => !item.product_id);
                if (emptyIndex >= 0) {
                    return prev.map((item, index) => index === emptyIndex
                        ? { ...applyProductToItem(item, product), quantity_received: 1 }
                        : item);
                }
                return [...prev, { ...applyProductToItem(createEmptyItem(), product), quantity_received: 1 }];
            });
            message.success(`นับสินค้า ${product.product_name || product.product_code} เพิ่มจำนวนอีก 1 แล้ว`);
        } catch (error) {
            console.error('Consignment barcode lookup failed', error);
            message.error('ค้นหาบาร์โค้ดไม่สำเร็จ กรุณาลองใหม่หรือเลือกสินค้าจากรายการ');
        } finally {
            setBarcodeInput('');
            setBarcodeSearching(false);
            window.setTimeout(() => barcodeInputRef.current?.focus(), 0);
        }
    };

    const handleItemFieldChange = (index: number, field: keyof FormProductItem, value: any) => {
        if (isLocked) return;
        setItems(prev => {
            const copy = [...prev];
            copy[index] = {
                ...copy[index],
                [field]: value
            };
            return copy;
        });
    };

    const summaryTotals = useMemo(() => {
        let totalQty = 0;
        let totalSales = 0;
        let totalPayable = 0;
        let totalProfit = 0;

        items.forEach(it => {
            const qty = Number(it.quantity_received || 0);
            const sell = Number(it.selling_price_per_unit || 0);
            const cost = Number(it.owner_cost_per_unit || 0);

            totalQty += qty;
            totalSales += (qty * sell);
            totalPayable += (qty * cost);
            totalProfit += (qty * (sell - cost));
        });

        return {
            totalQty,
            totalSales,
            totalPayable,
            totalProfit,
            itemCount: items.filter(it => it.product_id > 0).length
        };
    }, [items]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // A locked bill can only change its note (the Backend refuses
            // anything else), so send just that -- never the Batch rows.
            if (isLocked) {
                setLoading(true);
                await onSave({ note: values.note || '' });
                setLoading(false);
                onClose();
                return;
            }

            // Validate items
            if (items.length === 0) {
                message.error('กรุณาเพิ่มรายการสินค้าอย่างน้อย 1 รายการ');
                return;
            }

            const seenIds = new Set<number>();
            for (let i = 0; i < items.length; i++) {
                const it = items[i];
                if (!it.product_id || it.product_id === 0) {
                    message.error(`กรุณาเลือกสินค้าในรายการลำดับที่ ${i + 1}`);
                    return;
                }
                if (seenIds.has(it.product_id)) {
                    message.error(`พบสินค้าซ้ำกันในรอบการฝากขาย (ลำดับที่ ${i + 1})`);
                    return;
                }
                seenIds.add(it.product_id);

                if (it.quantity_received === undefined || it.quantity_received === null) {
                    message.error(`กรุณาระบุจำนวนรับฝากในลำดับที่ ${i + 1}`);
                    return;
                }
                if (!Number.isInteger(it.quantity_received)) {
                    message.error(`จำนวนรับฝากในลำดับที่ ${i + 1} ต้องเป็นจำนวนเต็ม (ไม่รองรับทศนิยม) เนื่องจากสินค้าฝากขายนับเป็นชิ้น`);
                    return;
                }
                if (it.quantity_received <= 0) {
                    message.error(`จำนวนรับฝากในลำดับที่ ${i + 1} ต้องมากกว่า 0`);
                    return;
                }
                if (it.selling_price_per_unit === undefined || it.selling_price_per_unit === null) {
                    message.error(`กรุณาระบุราคาขายหน้าร้านในลำดับที่ ${i + 1}`);
                    return;
                }
                if (it.owner_cost_per_unit === undefined || it.owner_cost_per_unit === null) {
                    message.error(`กรุณาระบุยอดจ่ายคืนเจ้าของในลำดับที่ ${i + 1}`);
                    return;
                }
            }

            setLoading(true);
            const payload = {
                vendor_id: values.vendor_id,
                consignment_date: values.consignment_date ? values.consignment_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
                note: values.note || '',
                items: items.map(it => ({
                    id: it.id,
                    product_id: it.product_id,
                    quantity_received: Number(it.quantity_received),
                    latest_actual_remaining: isLocked && it.latest_actual_remaining !== undefined
                        ? Number(it.latest_actual_remaining)
                        : Number(it.quantity_received),
                    selling_price_per_unit: Number(it.selling_price_per_unit || 0),
                    owner_cost_per_unit: Number(it.owner_cost_per_unit || 0)
                }))
            };

            await onSave(payload);
            setLoading(false);
            onClose();
        } catch (e: any) {
            setLoading(false);
            if (e?.errorFields) return;
            message.error(e?.message || 'บันทึกข้อมูลไม่สำเร็จ');
        }
    };

    const columns = [
        {
            title: '#',
            width: 45,
            render: (_: any, __: any, index: number) => index + 1
        },
        {
            title: 'สินค้าที่รับฝากขาย',
            dataIndex: 'product_id',
            key: 'product_id',
            width: 320,
            render: (_: any, record: FormProductItem, index: number) => (
                <Select
                    showSearch
                    disabled={isLocked}
                    suffixIcon={isLocked ? <span>🔒</span> : undefined}
                    placeholder="เลือกสินค้า..."
                    style={{ width: '100%' }}
                    value={record.product_id || undefined}
                    onChange={(val) => handleItemProductChange(index, val)}
                    filterOption={labelFilter}
                    onSearch={handleProductSearch}
                    options={productOptions}
                    data-testid={`select-product-${index}`}
                />
            )
        },
        {
            title: 'จำนวนรับฝาก',
            dataIndex: 'quantity_received',
            key: 'quantity_received',
            width: 120,
            render: (val: number, _: any, index: number) => (
                <InputNumber
                    min={1}
                    max={99999}
                    precision={0}
                    disabled={isLocked}
                    style={{ width: '100%' }}
                    value={val}
                    onChange={(v) => handleItemFieldChange(index, 'quantity_received', v === null ? undefined : Number(v))}
                    placeholder="0"
                    data-testid={`input-qty-${index}`}
                />
            )
        },
        {
            title: 'ราคาขายหน้าร้าน (บ.)',
            dataIndex: 'selling_price_per_unit',
            key: 'selling_price_per_unit',
            width: 130,
            render: (val: number, _: any, index: number) => (
                <InputNumber
                    min={0}
                    step={0.5}
                    disabled={isLocked}
                    style={{ width: '100%' }}
                    value={val}
                    onChange={(v) => handleItemFieldChange(index, 'selling_price_per_unit', v === null ? undefined : Number(v))}
                    placeholder="0.00"
                    data-testid={`input-selling-${index}`}
                />
            )
        },
        {
            title: 'จ่ายคืนเจ้าของ (บ.)',
            dataIndex: 'owner_cost_per_unit',
            key: 'owner_cost_per_unit',
            width: 130,
            render: (val: number, _: any, index: number) => (
                <InputNumber
                    min={0}
                    step={0.5}
                    disabled={isLocked}
                    style={{ width: '100%' }}
                    value={val}
                    onChange={(v) => handleItemFieldChange(index, 'owner_cost_per_unit', v === null ? undefined : Number(v))}
                    placeholder="0.00"
                    data-testid={`input-cost-${index}`}
                />
            )
        },
        {
            title: 'กำไร/หน่วย',
            width: 100,
            render: (_: any, r: FormProductItem) => {
                if (r.selling_price_per_unit === undefined || r.owner_cost_per_unit === undefined) {
                    return <span className="consignment-cell-sub">-</span>;
                }
                const profit = Number(r.selling_price_per_unit) - Number(r.owner_cost_per_unit);
                return (
                    <span style={{ color: profit >= 0 ? '#10B981' : '#EF4444', fontWeight: 700 }}>
                        ฿{profit.toFixed(2)}
                    </span>
                );
            }
        },
        {
            title: 'การดำเนินการ',
            width: 80,
            align: 'center' as const,
            render: (_: any, __: any, index: number) => !isLocked ? (
                <Tooltip title="ลบรายการนี้">
                    <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="table-action-button delete"
                        data-testid={`btn-delete-row-${index}`}
                    >
                        <FontAwesomeIcon icon="trash-can" />
                    </button>
                </Tooltip>
            ) : (
                <span style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 600 }}>🔒 ล็อก</span>
            )
        }
    ];

    return (
        <Modal
            open={open}
            title={editItem ? `✏️ แก้ไขรอบการฝากขาย: ${editItem.document_no}` : '📦 สร้างรอบการฝากขายใหม่ (Multi-item Consignment)'}
            onCancel={onClose}
            onOk={handleSubmit}
            confirmLoading={loading}
            okText="บันทึกรอบการฝากขาย"
            cancelText="ยกเลิก"
            width={1040}
            className="consignment-items-modal"
            destroyOnClose
        >
            {isLocked && (
                <div
                    className="consignment-locked-banner"
                    data-testid="consignment-locked-banner"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 14px',
                        marginBottom: '16px',
                        background: 'rgba(234, 179, 8, 0.12)',
                        border: '1px solid rgba(234, 179, 8, 0.35)',
                        borderRadius: '8px',
                        color: '#FDE047',
                        fontSize: '13px',
                        lineHeight: 1.4
                    }}
                >
                    <span style={{ fontSize: '16px' }}>🔒</span>
                    <span>
                        ข้อมูลต้นทางของรอบฝากขายนี้ถูกล็อกแล้ว เนื่องจากมีการนับ ฝากเพิ่ม หรือทำรายการหลังรับฝากแล้ว สามารถแก้ไขได้เฉพาะหมายเหตุ
                    </span>
                </div>
            )}

            <Form form={form} layout="vertical">
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                    <Form.Item
                        name="vendor_id"
                        label="ผู้ฝากขาย (Consignor / Vendor)"
                        rules={[{ required: true, message: 'กรุณาเลือกผู้ฝากขาย' }]}
                    >
                        <Select
                            showSearch
                            disabled={isLocked}
                            suffixIcon={isLocked ? <span>🔒</span> : undefined}
                            placeholder="เลือกหรือค้นหาผู้ฝากขาย..."
                            filterOption={labelFilter}
                            options={vendors.map(v => ({
                                value: v.id,
                                label: `[${v.code || 'V'}] ${v.name} ${v.tel ? `(${v.tel})` : ''}`
                            }))}
                            data-testid="select-vendor"
                        />
                    </Form.Item>

                    <Form.Item
                        name="consignment_date"
                        label="วันที่รับฝากขาย"
                        rules={[{ required: true, message: 'กรุณาเลือกวันที่' }]}
                    >
                        <DatePicker
                            disabled={isLocked}
                            suffixIcon={isLocked ? <span>🔒</span> : undefined}
                            style={{ width: '100%' }}
                            format="YYYY-MM-DD"
                            data-testid="picker-date"
                        />
                    </Form.Item>
                </div>

                <div className="consignment-items-section-header">
                    <h3 className="consignment-items-section-title" data-testid="consignment-items-section-title">
                        📋 รายการสินค้าในรอบนี้ ({items.length} รายการ)
                    </h3>
                    {editItem && (editItem.items || []).some(it => !isOriginalDeposit(it)) && (
                        <div className="consignment-cell-sub" data-testid="consignment-edit-original-only">
                            แสดงเฉพาะสินค้าที่รับฝากตอนเปิดบิล — ชุดที่ฝากเพิ่มหรือยกมาจากรอบก่อน ดูได้ในรายละเอียดบิล
                        </div>
                    )}
                    <div className="consignment-items-section-actions">
                        {!editItem && !isLocked && (
                            <Input
                                ref={barcodeInputRef}
                                className="consignment-barcode-lookup"
                                value={barcodeInput}
                                onChange={event => setBarcodeInput(event.target.value)}
                                onPressEnter={() => handleBarcodeLookup(barcodeInput)}
                                placeholder="สแกน/กรอกบาร์โค้ด แล้วกด Enter"
                                aria-label="บาร์โค้ดสินค้า"
                                data-testid="input-consignment-barcode"
                                prefix={<FontAwesomeIcon icon="barcode" />}
                                allowClear
                                autoComplete="off"
                            />
                        )}
                        {!isLocked && (
                            <Button
                                type="dashed"
                                onClick={handleAddItem}
                                icon={<FontAwesomeIcon icon="plus" style={{ marginRight: '6px' }} />}
                                data-testid="btn-add-product-row"
                            >
                                เพิ่มสินค้า
                            </Button>
                        )}
                    </div>
                </div>

                <Table
                    className="consignment-items-table"
                    tableLayout="fixed"
                    columns={columns}
                    dataSource={items.map((it, idx) => ({ ...it, key: idx }))}
                    pagination={false}
                    size="small"
                    bordered
                    style={{ marginBottom: '16px' }}
                />

                {/* Live Multi-item Calculation Summary Box */}
                <div className="consignment-calc-box">
                    <div className="consignment-calc-title">
                        📊 สรุปประมาณการทั้งรอบบิล ({summaryTotals.itemCount} รายการ, รวม {summaryTotals.totalQty} ชิ้น)
                    </div>
                    <div className="consignment-calc-grid">
                        <div className="consignment-calc-item">
                            <span className="calc-label">ยอดขายรวมทั้งหมด:</span>
                            <span className="calc-val sales">฿{summaryTotals.totalSales.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="consignment-calc-item">
                            <span className="calc-label">ยอดค้างจ่ายคืนเจ้าของ:</span>
                            <span className="calc-val payable">฿{summaryTotals.totalPayable.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="consignment-calc-item">
                            <span className="calc-label">กำไรส่วนแบ่งร้านค้า:</span>
                            <span className="calc-val profit">฿{summaryTotals.totalProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                    </div>
                </div>

                <Form.Item name="note" label="บันทึกช่วยจำ / หมายเหตุรอบการฝากขาย">
                    <Input.TextArea rows={2} placeholder="เช่น ฝากขายรอบสัปดาห์, วางชั้นหน้าเคาน์เตอร์..." />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ConsignmentModal;
